import React, { useEffect, useState } from "react";
import { useBuilding } from "../../context/BuildingContext";
import { reportsApi } from "../../services/api";
import GenericReportViewer from "./GenericReportViewer";

interface Props {
  period: string;
}

const ALL_KEYS: { key: string; label: string }[] = [
  { key: "budget-vs-actual", label: "Budget vs Actual" },
  { key: "income-expenditure", label: "Income & Expenditure" },
  { key: "balance-sheet", label: "Balance Sheet" },
  { key: "trial-balance", label: "Trial Balance" },
  { key: "bank-reconciliation", label: "Bank Reconciliation" },
  { key: "aged-debtors", label: "Aged Debtors" },
  { key: "aged-creditors", label: "Aged Creditors" },
  { key: "apportionment", label: "Service Charge Apportionment" },
  { key: "spending-categories", label: "Spending Categories" },
  { key: "capital-assets", label: "Capital Assets" },
  { key: "reserves", label: "Reserve Fund" },
  { key: "year-end", label: "Year-End Accounts" },
];

const DEFAULT_SELECTED = [
  "budget-vs-actual",
  "income-expenditure",
  "balance-sheet",
  "aged-debtors",
  "aged-creditors",
  "reserves",
];

const ManagementPackReport: React.FC<Props> = ({ period }) => {
  const { currentBuilding } = useBuilding();
  const [selected, setSelected] = useState<string[]>(DEFAULT_SELECTED);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<any>(null);

  const toggle = (key: string) =>
    setSelected((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );

  const load = async () => {
    if (!currentBuilding?.id) return;
    try {
      setLoading(true);
      setError(null);
      setReport(
        await reportsApi.getManagementPack(currentBuilding.id, {
          period,
          reports: selected.join(","),
        }),
      );
    } catch (e) {
      console.error(e);
      setError("Failed to load management pack.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="bg-white shadow rounded-lg p-4 mb-4">
        <h3 className="text-sm font-semibold text-gray-700 uppercase mb-3">
          Reports to include
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
          {ALL_KEYS.map(({ key, label }) => (
            <label key={key} className="inline-flex items-center text-sm">
              <input
                type="checkbox"
                className="mr-2"
                checked={selected.includes(key)}
                onChange={() => toggle(key)}
              />
              {label}
            </label>
          ))}
        </div>
        <button
          onClick={load}
          disabled={loading || !selected.length}
          className="bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white px-4 py-2 rounded"
        >
          {loading ? "Generating…" : "Generate pack"}
        </button>
      </div>
      {(loading || error || report) && (
        <GenericReportViewer
          title="Management Report Pack"
          loading={loading}
          error={error}
          report={report}
        />
      )}
    </div>
  );
};

export default ManagementPackReport;
